import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck, User, LayoutGrid, X, MoreHorizontal, ChevronDown as ChevronDownP, Bell, BellOff, Smartphone, Pencil, Phone } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Field, TAGLIE, fmtData, blurSuTouch } from "@/components/Shared";
import { useAuth, RUOLI_LABEL } from "@/context/AuthContext";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { NAV } from "@/components/Layout";
import { pushSupportato, iosNonInstallato, eIscritto, attivaNotifiche, disattivaNotifiche } from "@/lib/pushNotifiche";

const SESSO_LABEL = { M: "Maschile (M)", F: "Femminile (F)" };

const CAMPI = [
  { chiave: "data_nascita", label: "Data di nascita", tipo: "date" },
  { chiave: "telefono", label: "Telefono", tipo: "tel", placeholder: "Es. 333 1234567" },
  { chiave: "sesso", label: "Sesso", tipo: "select", opzioni: (d) => d.sessi || ["M", "F"], labelOpzione: (v) => SESSO_LABEL[v] || v, hint: "Serve per la generazione delle stanze del camposcuola" },
  { chiave: "taglia_maglia", label: "Taglia maglia", tipo: "select", hint: "Serve per l'ordine delle maglie" },
];

// Campi sanitari: NON stanno in CAMPI perché non seguono la regola "si compila
// una volta e si blocca". Un'allergia si può scoprire in qualsiasi momento, e
// correggerla non può richiedere di rintracciare un Responsabile.
const CAMPI_SANITARI = [
  { chiave: "intolleranze", label: "Intolleranze e allergie", tipo: "textarea",
    placeholder: "Es. intollerante al lattosio, allergia alle noci...",
    hint: "Serve alla gestione dei pasti del camposcuola. Lo vedono solo i Responsabili incaricati dei pasti — non tutti." },
];

export default function Profilo() {
  const [dati, setDati] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false); // admin/resp: modalità modifica

  const load = useCallback(() => {
    api.get("/profilo").then((r) => {
      setDati(r.data);
      // Campi editabili pre-compilati col valore attuale (admin/resp li ri-modificano,
      // gli animatori vedono vuoti quelli non ancora compilati)
      const iniziale = {};
      CAMPI.forEach(({ chiave }) => { if (!r.data.bloccati[chiave]) iniziale[chiave] = r.data.campi[chiave] || ""; });
      // I sanitari arrivano separati e sono sempre editabili
      CAMPI_SANITARI.forEach(({ chiave }) => { iniziale[chiave] = r.data.campi_sanitari?.[chiave] || ""; });
      setForm(iniziale);
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  // Admin/Responsabili aprono la modifica coi valori attuali pre-compilati
  const apriModifica = () => {
    const iniziale = {};
    CAMPI.forEach(({ chiave }) => { iniziale[chiave] = (dati.campi[chiave] || ""); });
    CAMPI_SANITARI.forEach(({ chiave }) => { iniziale[chiave] = (dati.campi_sanitari?.[chiave] || ""); });
    setForm(iniziale);
    setEditMode(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    const libero = !!dati.puo_modificare;
    const payload = {};
    if (libero) {
      // Admin/Responsabili: invia tutti i campi (anche vuoti = svuota)
      Object.entries(form).forEach(([k, v]) => { payload[k] = String(v || "").trim(); });
    } else {
      Object.entries(form).forEach(([k, v]) => { if (v && String(v).trim()) payload[k] = String(v).trim(); });
      if (Object.keys(payload).length === 0) {
        toast.error("Compila almeno un campo prima di salvare");
        return;
      }
    }
    setSaving(true);
    try {
      await api.put("/profilo", payload);
      toast.success("Profilo aggiornato");
      setEditMode(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (!dati) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  // Un minorenne non può prestare validamente il consenso a un dato sanitario:
  // finché non è registrata l'autorizzazione di un genitore, il campo
  // `intolleranze` non è compilabile da lui. Il server lo rifiuta comunque;
  // qui lo si toglie dal modulo, così il divieto si vede prima di provare.
  const intolleranzeBloccate = !!dati.intolleranze_bloccate;
  const compilabile = (c) => !dati.bloccati[c.chiave] && !(intolleranzeBloccate && c.chiave === "intolleranze");

  const daCompilare = CAMPI.filter(compilabile);
  const compilati = CAMPI.filter((c) => dati.bloccati[c.chiave]);

  const valoreLeggibile = (c) => {
    const v = dati.campi[c.chiave];
    if (!v) return "—";
    if (c.chiave === "data_nascita") return fmtData(v);
    if (c.chiave === "sesso") return SESSO_LABEL[v] || v;
    return v;
  };

  return (
    <div data-testid="profilo-page">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Profilo</h1>
        <p className="text-sm text-slate-500 font-semibold">Le tue informazioni personali</p>
      </div>

      {/* Anagrafica dell'account: non modificabile da qui */}
      <div className="card-app p-5 mb-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <User className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-display font-extrabold text-slate-900 truncate">{dati.nome} {dati.cognome}</p>
            <p className="text-xs text-slate-400 font-semibold truncate">
              {RUOLI_LABEL[dati.role] || dati.role} · {dati.username}
            </p>
          </div>
        </div>
      </div>

      {/* Notifiche push su questo dispositivo */}
      <CardNotifiche />

      {/* Modulo editabile (usato da: animatori con campi da compilare, o admin/resp in modifica) */}
      {(() => {
        const CampoInput = (c) => (
          <Field key={c.chiave} label={`${c.label} (facoltativo)`} hint={c.hint}>
            {c.tipo === "select" ? (
              <select className="input-app" value={form[c.chiave] ?? ""}
                onChange={(e) => { setForm({ ...form, [c.chiave]: e.target.value }); blurSuTouch(e); }}
                data-testid={`profilo-${c.chiave}-input`}>
                <option value="">Non indicata</option>
                {(c.opzioni ? c.opzioni(dati) : (dati.taglie || TAGLIE)).map((t) => <option key={t} value={t}>{c.labelOpzione ? c.labelOpzione(t) : t}</option>)}
              </select>
            ) : c.tipo === "textarea" ? (
              <textarea className="input-app" rows={2} placeholder={c.placeholder}
                value={form[c.chiave] ?? ""} onChange={(e) => setForm({ ...form, [c.chiave]: e.target.value })}
                data-testid={`profilo-${c.chiave}-input`} />
            ) : (
              <input type={c.tipo} className="input-app" placeholder={c.placeholder}
                value={form[c.chiave] ?? ""} onChange={(e) => setForm({ ...form, [c.chiave]: e.target.value })}
                data-testid={`profilo-${c.chiave}-input`} />
            )}
          </Field>
        );

        // ---- ADMIN / RESPONSABILI: vista con pulsante Modifica, oppure form ----
        if (dati.puo_modificare) {
          if (editMode) {
            return (
              <>
                <p className="label-app">Modifica i tuoi dati</p>
                <div className="card-app p-5">
                  <form onSubmit={salva}>
                    {CAMPI.map(CampoInput)}
                    {/* Sanitario: sempre modificabile anche qui */}
                    {CAMPI_SANITARI.map(CampoInput)}
                    <div className="flex gap-2 mt-1">
                      <button className="btn-primary flex-1" disabled={saving} data-testid="profilo-submit-btn">
                        {saving ? "Salvataggio..." : "Salva"}
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => setEditMode(false)} data-testid="profilo-annulla-btn">
                        Annulla
                      </button>
                    </div>
                  </form>
                </div>
              </>
            );
          }
          return (
            <>
              <p className="label-app">I tuoi dati</p>
              <div className="card-app divide-y divide-slate-50 mb-3" data-testid="profilo-dati">
                {CAMPI.map((c) => (
                  <div key={c.chiave} className="px-5 py-3.5" data-testid={`profilo-campo-${c.chiave}`}>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{c.label}</p>
                    <p className="text-sm font-semibold text-slate-700 break-words">{valoreLeggibile(c)}</p>
                  </div>
                ))}
              </div>
              <button type="button" className="btn-ghost" onClick={apriModifica} data-testid="profilo-modifica-btn">
                <Pencil className="h-4 w-4" /> Modifica profilo
              </button>
            </>
          );
        }

        // ---- ANIMATORI / AIUTI: compilati bloccati + campi da compilare ----
        return (
          <>
            {/* Sanitari: riquadro a parte, sempre modificabile. Non compare fra
                i "Dati salvati" bloccati, perché bloccarlo sarebbe pericoloso. */}
            {!intolleranzeBloccate && CAMPI_SANITARI.map((c) => (
              <div key={c.chiave} className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 mb-5" data-testid={`profilo-sanitario-${c.chiave}`}>
                <p className="text-sm font-bold text-slate-800 mb-1">{c.label}</p>
                <p className="text-xs text-slate-600 font-semibold mb-2">{c.hint}</p>
                <textarea className="input-app" rows={2} placeholder={c.placeholder}
                  value={form[c.chiave] ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, [c.chiave]: e.target.value }))}
                  data-testid={`profilo-input-${c.chiave}`} />
                <p className="text-[11px] text-slate-500 font-semibold mt-1">
                  Puoi correggerlo quando vuoi, anche più volte: a differenza degli altri campi
                  questo non si blocca mai.
                </p>
              </div>
            ))}

            {intolleranzeBloccate && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-5" data-testid="avviso-intolleranze-bloccate">
                <p className="text-sm font-bold text-slate-800 mb-1">Allergie e intolleranze</p>
                <p className="text-xs text-slate-700 font-medium leading-relaxed">
                  Questo campo riguarda la tua salute, e finché sei minorenne non puoi
                  compilarlo da solo: serve il consenso di un tuo genitore. Appena un
                  responsabile ha registrato l'autorizzazione firmata, potrai inserirlo —
                  oppure lo inserirà lui insieme a un tuo genitore.
                </p>
              </div>
            )}

            {compilati.length > 0 && (
              <>
                <p className="label-app">Dati salvati</p>
                <div className="card-app divide-y divide-slate-50 mb-5" data-testid="profilo-bloccati">
                  {compilati.map((c) => (
                    <div key={c.chiave} className="flex items-center justify-between gap-3 px-5 py-3.5" data-testid={`profilo-bloccato-${c.chiave}`}>
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{c.label}</p>
                        <p className="text-sm font-semibold text-slate-700 break-words">{valoreLeggibile(c)}</p>
                      </div>
                      <Lock className="h-4 w-4 text-slate-300 shrink-0" />
                    </div>
                  ))}
                </div>
                <div className="rounded-3xl bg-sky-50 border border-sky-200 p-4 mb-5 flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                  <p className="text-xs font-semibold text-sky-800">
                    I dati salvati non sono più modificabili da qui. Se qualcosa non è corretto, chiedi a un
                    Responsabile: può correggerlo lui dalla sezione Animatori.
                  </p>
                </div>
              </>
            )}
            {daCompilare.length > 0 ? (
              <>
                <p className="label-app">Da compilare</p>
                <div className="card-app p-5">
                  <p className="text-xs text-slate-400 font-semibold mb-4">
                    Tutti i campi sono facoltativi, ma una volta salvati non potrai più modificarli da solo.
                  </p>
                  <form onSubmit={salva}>
                    {daCompilare.map(CampoInput)}
                    <button className="btn-primary w-full" disabled={saving} data-testid="profilo-submit-btn">
                      {saving ? "Salvataggio..." : "Salva"}
                    </button>
                    <p className="text-[11px] text-slate-400 font-semibold text-center mt-2">
                      Verranno salvati solo i campi che hai compilato.
                    </p>
                  </form>
                </div>
              </>
            ) : (
              <div className="card-app p-6 text-center" data-testid="profilo-completo">
                <ShieldCheck className="mx-auto h-10 w-10 text-emerald-400 mb-2" strokeWidth={1.5} />
                <p className="font-display font-bold text-slate-600">Profilo completo</p>
                <p className="text-sm text-slate-400 font-semibold">Hai compilato tutte le informazioni.</p>
              </div>
            )}
          </>
        );
      })()}

      {/* Come farsi contattare: solo per chi viene mostrato ad altri */}
      <div className="mt-5">
        <CardContatto />
      </div>

      {/* I miei dispositivi: dove è aperto il proprio account */}
      <div className="mt-5">
        <CardDispositivi />
      </div>

      {/* Personalizzazione della barra: in fondo, collassabile */}
      <div className="mt-5">
        <PersonalizzaBarra />
      </div>
    </div>
  );
}


/**
 * «Come ti contattano gli animatori» (lotto 10.2).
 *
 * Compare solo a Responsabili e Amministratori: sono gli unici il cui recapito
 * viene mostrato a qualcun altro. Offrire a un animatore di nascondere un
 * numero che nessuno vede sarebbe una promessa senza oggetto.
 *
 * Due possibilità, in ordine di preferenza: indicare un secondo numero (la
 * segreteria, una SIM dedicata) oppure opporsi del tutto. L'opposizione non
 * richiede una motivazione — è l'art. 21, e per un cellulare personale è
 * ragionevole non volerlo far girare fra trenta ragazzi.
 */
function CardContatto() {
  const [dati, setDati] = useState(null);
  const [alt, setAlt] = useState("");
  const [nascosto, setNascosto] = useState(false);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(() => {
    api.get("/profilo").then((r) => {
      setDati(r.data);
      setAlt(r.data?.contatto?.recapito_alternativo || "");
      setNascosto(!!r.data?.contatto?.telefono_nascosto);
    }).catch(() => setDati(null));
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salva = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/profilo", patch);
      setAlt(data?.contatto?.recapito_alternativo || "");
      setNascosto(!!data?.contatto?.telefono_nascosto);
      toast.success("Preferenza salvata");
      carica();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  if (!dati?.contatto?.rilevante) return null;
  const mostrato = dati.contatto.mostrato;

  return (
    <div className="card-app p-5" data-testid="profilo-contatto">
      <p className="flex items-center gap-2 font-display font-extrabold text-slate-900">
        <Phone className="h-5 w-5 text-amber-500" /> Come ti contattano gli animatori
      </p>
      <p className="text-xs text-slate-500 font-semibold mt-1 leading-relaxed">
        Gli animatori del tuo gruppo vedono un tuo recapito per poterti chiamare durante
        l'attività. Puoi scegliere quale, oppure non mostrarne nessuno.
      </p>

      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 mt-3">
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
          Oggi vedono
        </p>
        <p className="text-sm font-bold text-slate-800" data-testid="contatto-mostrato">
          {mostrato || "nessun numero"}
        </p>
      </div>

      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mt-4 mb-1">
        Recapito alternativo
      </label>
      <div className="flex gap-2 flex-wrap items-center">
        <input className="input-app !py-2 max-w-[15rem]" value={alt} inputMode="tel"
          onChange={(e) => setAlt(e.target.value)}
          placeholder="es. numero della segreteria" data-testid="contatto-alt-input" />
        <button type="button" className="btn-ghost !py-2 text-xs" disabled={busy}
          onClick={() => salva({ recapito_alternativo: alt })}
          data-testid="contatto-alt-salva">Salva</button>
      </div>
      <p className="text-[11px] text-slate-400 font-semibold mt-1">
        Se lo compili, viene mostrato questo al posto del tuo numero personale.
      </p>

      <label className="flex items-start gap-2 mt-4 text-sm font-semibold text-slate-700">
        <input type="checkbox" checked={nascosto} className="mt-1" disabled={busy}
          onChange={(e) => salva({ telefono_nascosto: e.target.checked })}
          data-testid="contatto-nascondi" />
        <span>
          Non mostrare il mio numero personale agli animatori
          <span className="block text-[11px] font-semibold text-slate-400">
            I Responsabili e l'Amministratore continuano a vederlo per l'organizzazione.
          </span>
        </span>
      </label>
    </div>
  );
}


/**
 * «I miei dispositivi»: le sessioni aperte con il proprio account.
 *
 * Ognuno vede SOLO le proprie — nemmeno l'Amministratore può guardare quelle
 * degli altri. Sarebbe una cronologia di quando e da dove ciascuno si collega,
 * che per un animatore di quattordici anni non deve esistere.
 *
 * Di ogni sessione si mostra il minimo che serve a riconoscerla: il tipo di
 * dispositivo e la rete, senza versioni né indirizzo completo. Serve a
 * rispondere a «questo accesso l'ho fatto io?», non a localizzare nessuno.
 */
function CardDispositivi() {
  const [dati, setDati] = useState(null);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(() => {
    api.get("/auth/dispositivi").then((r) => setDati(r.data)).catch(() => setDati(null));
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const chiudi = async (d) => {
    setBusy(true);
    try {
      await api.delete(`/auth/dispositivi/${d.id}`);
      toast.success("Accesso chiuso");
      carica();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const chiudiTutti = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/dispositivi/chiudi-tutti");
      toast.success(data.chiuse ? `${data.chiuse} accessi chiusi` : "Nessun altro accesso aperto");
      carica();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  if (!dati) return null;
  const altri = (dati.dispositivi || []).filter((d) => !d.corrente).length;

  return (
    <div className="card-app p-5" data-testid="profilo-dispositivi">
      <p className="flex items-center gap-2 font-display font-extrabold text-slate-900">
        <Smartphone className="h-5 w-5 text-amber-500" /> I miei dispositivi
      </p>
      <p className="text-xs text-slate-500 font-semibold mt-1 leading-relaxed">
        Dove il tuo account è aperto in questo momento. Se vedi un accesso che non
        riconosci, chiudilo e cambia la password.
        {dati.ore_inattivita <= 48 && (
          <> Il tuo accesso si chiude da solo dopo {dati.ore_inattivita} ore che non usi l'app.</>
        )}
      </p>

      <div className="mt-4 space-y-2">
        {(dati.dispositivi || []).map((d) => (
          <div key={d.id}
            className={`rounded-2xl border p-3 flex items-start justify-between gap-3 flex-wrap ${
              d.corrente ? "border-emerald-200 bg-emerald-50" : "border-slate-200"
            }`}
            data-testid={`dispositivo-${d.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">
                {d.dispositivo}
                {d.corrente && (
                  <span className="ml-2 text-[11px] font-bold text-emerald-700">
                    questo dispositivo
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500 font-semibold">
                Rete {d.rete} · ultimo utilizzo{" "}
                {d.ultimo_uso ? new Date(d.ultimo_uso).toLocaleString("it-IT") : "—"}
              </p>
            </div>
            {!d.corrente && (
              <button type="button" className="btn-ghost !py-1.5 text-xs" disabled={busy}
                onClick={() => chiudi(d)} data-testid={`dispositivo-chiudi-${d.id}`}>
                <X className="h-3.5 w-3.5" /> Chiudi
              </button>
            )}
          </div>
        ))}
      </div>

      {altri > 0 && (
        <button type="button" className="btn-ghost !py-2 text-xs mt-3" disabled={busy}
          onClick={chiudiTutti} data-testid="dispositivi-chiudi-tutti">
          Chiudi tutti gli altri accessi ({altri})
        </button>
      )}
    </div>
  );
}

/**
 * Card notifiche push: attiva/disattiva su QUESTO dispositivo.
 * Gestisce i casi: non supportato, iOS non installato, permesso negato.
 */
function CardNotifiche() {
  const [stato, setStato] = useState("caricamento"); // attive | disattive | negato | ios | non-supportato
  const [busy, setBusy] = useState(false);

  const aggiorna = useCallback(async () => {
    if (!pushSupportato()) { setStato(iosNonInstallato() ? "ios" : "non-supportato"); return; }
    if (Notification.permission === "denied") { setStato("negato"); return; }
    setStato((await eIscritto()) ? "attive" : "disattive");
  }, []);
  useEffect(() => { aggiorna(); }, [aggiorna]);

  const attiva = async () => {
    setBusy(true);
    try { await attivaNotifiche(); toast.success("Notifiche attivate su questo dispositivo"); await aggiorna(); }
    catch (err) { toast.error(err.message || "Impossibile attivare le notifiche"); await aggiorna(); }
    finally { setBusy(false); }
  };
  const disattiva = async () => {
    setBusy(true);
    try { await disattivaNotifiche(); toast.success("Notifiche disattivate"); await aggiorna(); }
    catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-app p-5 mb-5" data-testid="card-notifiche">
      <div className="flex items-start gap-3">
        <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${stato === "attive" ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
          {stato === "attive" ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-800 text-sm">Notifiche</p>
          <p className="text-xs text-slate-400 font-semibold">
            {stato === "attive" && "Attive su questo dispositivo. Ricevi il programma del giorno e gli avvisi in bacheca."}
            {stato === "disattive" && "Ricevi una notifica quando esce il programma del giorno o un nuovo avviso."}
            {stato === "negato" && "Hai bloccato le notifiche. Riattivale dalle impostazioni del telefono per questa app."}
            {stato === "ios" && "Per ricevere le notifiche su iPhone, installa prima l'app sulla schermata Home."}
            {stato === "non-supportato" && "Questo dispositivo non supporta le notifiche."}
            {stato === "caricamento" && "…"}
          </p>
        </div>
      </div>
      {stato === "ios" && (
        <p className="text-[11px] text-slate-400 font-semibold mt-3 flex items-center gap-1.5">
          <Smartphone className="h-3.5 w-3.5" /> Safari → Condividi → «Aggiungi a Home»
        </p>
      )}
      {(stato === "attive" || stato === "disattive") && (
        <button type="button" onClick={stato === "attive" ? disattiva : attiva} disabled={busy}
          className={`mt-4 ${stato === "attive" ? "btn-ghost" : "btn-primary"} !py-2 text-xs`} data-testid="notifiche-toggle-btn">
          {busy ? "Attendere..." : stato === "attive" ? "Disattiva notifiche" : "Attiva notifiche"}
        </button>
      )}
    </div>
  );
}

const MAX_SLOT = 5;

/**
 * "Personalizza barra": l'utente sceglie fino a 5 sezioni per la barra in
 * basso (mobile). "Altro" resta sempre fisso a destra. Le scelte si salvano
 * sul server, così restano su ogni dispositivo.
 */
function PersonalizzaBarra() {
  const { user, setUser } = useAuth();

  // Sezioni disponibili per il ruolo dell'utente (Classifica esclusa: la sua
  // visibilità dipende dallo stato "pubblica", non la mettiamo tra le scelte).
  const disponibili = useMemo(
    () => NAV.filter((n) => n.roles.includes(user.role) && n.to !== "/classifica"),
    [user.role]
  );
  const byPath = useMemo(() => Object.fromEntries(disponibili.map((n) => [n.to, n])), [disponibili]);

  const iniziale = (Array.isArray(user.nav_preferita) ? user.nav_preferita : [])
    .filter((p) => byPath[p]);
  const [scelte, setScelte] = useState(iniziale);
  const [salvando, setSalvando] = useState(false);

  const nonScelte = disponibili.filter((n) => !scelte.includes(n.to));
  const modificato =
    scelte.length !== iniziale.length || scelte.some((p, i) => p !== iniziale[i]);

  const aggiungi = (p) => setScelte((s) => (s.length >= MAX_SLOT ? s : [...s, p]));
  const rimuovi = (p) => setScelte((s) => s.filter((x) => x !== p));
  const sposta = (i, dir) =>
    setScelte((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const c = [...s];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });

  const salva = async () => {
    setSalvando(true);
    try {
      const { data } = await api.put("/profilo/navbar", { paths: scelte });
      setUser({ ...user, nav_preferita: data.nav_preferita });
      toast.success("Barra aggiornata");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSalvando(false); }
  };

  const ripristina = async () => {
    setSalvando(true);
    try {
      await api.put("/profilo/navbar", { paths: [] });
      setUser({ ...user, nav_preferita: [] });
      setScelte([]);
      toast.success("Barra ripristinata al default");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSalvando(false); }
  };

  const [aperta, setAperta] = useState(false);

  return (
    <div className="card-app overflow-hidden" data-testid="personalizza-barra">
      <button type="button" onClick={() => setAperta((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/60 transition-colors"
        aria-expanded={aperta} data-testid="personalizza-barra-toggle">
        <LayoutGrid className="h-4 w-4 text-amber-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-800 text-sm">Barra in basso</p>
          {!aperta && (
            <p className="text-xs text-slate-400 font-semibold truncate">
              {scelte.length > 0 ? `${scelte.length} sezioni scelte` : "Barra predefinita per il tuo ruolo"}
            </p>
          )}
        </div>
        <ChevronDownP className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${aperta ? "rotate-180" : ""}`} />
      </button>

      {aperta && (
      <div className="px-4 pb-4">
      <p className="text-xs text-slate-400 font-semibold mb-4">
        Scegli fino a {MAX_SLOT} sezioni da tenere a portata di mano sul telefono. Il pulsante
        “Altro” resta sempre a destra per raggiungere tutto il resto.
      </p>

      {/* Anteprima delle scelte, in ordine */}
      {scelte.length === 0 ? (
        <p className="text-xs text-slate-400 font-semibold rounded-2xl bg-slate-50 p-3 mb-4">
          Nessuna scelta: viene usata la barra predefinita per il tuo ruolo.
        </p>
      ) : (
        <div className="space-y-2 mb-4">
          {scelte.map((p, i) => {
            const n = byPath[p];
            if (!n) return null;
            return (
              <div key={p} className="flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2" data-testid={`slot-${p.slice(1)}`}>
                <span className="text-xs font-extrabold text-slate-400 w-5 text-center shrink-0">{i + 1}</span>
                <n.icon className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="text-sm font-bold text-slate-700 flex-1 min-w-0 truncate">{n.label}</span>
                <button type="button" onClick={() => sposta(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30 px-1" aria-label="Su">▲</button>
                <button type="button" onClick={() => sposta(i, 1)} disabled={i === scelte.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30 px-1" aria-label="Giù">▼</button>
                <button type="button" onClick={() => rimuovi(p)} className="text-slate-400 hover:text-red-500 shrink-0" aria-label="Rimuovi" data-testid={`slot-remove-${p.slice(1)}`}><X className="h-4 w-4" /></button>
              </div>
            );
          })}
          {/* Lo slot fisso "Altro" */}
          <div className="flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 opacity-70">
            <span className="text-xs font-extrabold text-slate-400 w-5 text-center shrink-0">•</span>
            <MoreHorizontal className="h-4 w-4 text-slate-500 shrink-0" />
            <span className="text-sm font-bold text-slate-500 flex-1">Altro (sempre presente)</span>
          </div>
        </div>
      )}

      {/* Sezioni aggiungibili */}
      {scelte.length < MAX_SLOT && nonScelte.length > 0 && (
        <div className="mb-4">
          <p className="label-app">Aggiungi ({scelte.length}/{MAX_SLOT})</p>
          <div className="flex flex-wrap gap-2">
            {nonScelte.map((n) => (
              <button key={n.to} type="button" onClick={() => aggiungi(n.to)}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:border-amber-400 px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors"
                data-testid={`add-${n.to.slice(1)}`}>
                <n.icon className="h-3.5 w-3.5" /> {n.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button type="button" className="btn-primary !py-2 text-xs" onClick={salva} disabled={salvando || !modificato} data-testid="barra-salva-btn">
          {salvando ? "Salvataggio..." : "Salva barra"}
        </button>
        {(iniziale.length > 0 || scelte.length > 0) && (
          <button type="button" className="btn-ghost !py-2 text-xs" onClick={ripristina} disabled={salvando} data-testid="barra-ripristina-btn">
            Usa il default
          </button>
        )}
      </div>
      </div>
      )}
    </div>
  );
}
